import {
  useDismissNotification,
  useStatusNotifications,
} from "features/notifications/hooks";
import type {
  StatusNotification,
  StatusNotificationStatus,
} from "features/notifications/types";
import { AnimatePresence, motion } from "framer-motion";
import { useTheme, Toast, Portal, styled, Icon, Stack } from "prism-react";
import type { FC } from "react";
import React, { useLayoutEffect, useEffect } from "react";

export const ToastsPosition = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  position: "fixed",
  bottom: "$4",
  left: 0,
  right: 0,
});

const getAnimation = () => {
  const position = {
    opacity: 1,
    y: 0,
    scale: 1,
  };

  return {
    ...position,
  };
};

const ToastAnimation: React.FC<{
  layoutId: string;
  children: React.ReactNode;
}> = ({ children, layoutId }) => {
  return (
    <motion.div
      animate={getAnimation()}
      exit={{ opacity: 0 }}
      initial={{
        opacity: 0,
        y: 35,
        scale: 0.98,
      }}
      layoutId={layoutId}
      layout
    >
      {children}
    </motion.div>
  );
};

const TOAST_CLOSE_TIMEOUT = 3000;

function getVariant(status: StatusNotificationStatus) {
  return status;
}

const SelfDismissingToast: FC<StatusNotification> = ({
  id,
  message,
  status,
  actions,
  timeout = TOAST_CLOSE_TIMEOUT,
  icon,
  callback,
}) => {
  const dismiss = useDismissNotification();

  useEffect(() => {
    const t = window.setTimeout(() => {
      dismiss(id);
      callback?.();
    }, timeout);

    return () => window.clearTimeout(t);
  }, [dismiss, id]);

  return (
    <Toast actions={actions} variant={getVariant(status)}>
      <Stack gap={2} horizontal>
        {icon && <Icon size={4} type={icon} />}
        <span>{message}</span>
      </Stack>
    </Toast>
  );
};

export const ToastStack: React.FC = () => {
  const notifications = useStatusNotifications();
  const dismiss = useDismissNotification();

  /**
   * Store the last rendered notification in a ref, dismissing it when a new notification is rendered.
   */
  const notification = notifications[notifications.length - 1];
  const notificationId = notification?.id;
  const lastRenderedIdRef = React.useRef<string>();

  useLayoutEffect(() => {
    const lastRenderedId = lastRenderedIdRef.current;

    if (!lastRenderedId) return;

    if (notificationId !== lastRenderedId) {
      dismiss(lastRenderedId);
      lastRenderedIdRef.current = notificationId;
    }
  }, [dismiss, notificationId]);

  const theme = useTheme();

  return (
    <Portal>
      <div className={theme.className}>
        <ToastsPosition>
          <AnimatePresence>
            {notification && (
              <ToastAnimation key={notification.id} layoutId={notification.id}>
                <SelfDismissingToast {...notification} />
              </ToastAnimation>
            )}
          </AnimatePresence>
        </ToastsPosition>
      </div>
    </Portal>
  );
};
